import { movies } from "@/mocks/movies";
import { tvs } from "@/mocks/tvs";
import { specials } from "@/mocks/specials";
import type { Film, FilmReview, FilmType, Special } from "@/types/film";

export const films: Film[] = [...(movies as Film[]), ...(tvs as Film[])];

export const specialList: Special[] = specials as Special[];

export const typeLabels: Record<FilmType, string> = {
  movie: "电影",
  tv: "电视剧",
};

export function getFilmById(id?: string): Film | undefined {
  if (!id) return undefined;
  return films.find((film) => film.id === id);
}

export function getSpecialById(id?: string): Special | undefined {
  if (!id) return undefined;
  return specialList.find((item) => item.id === id);
}

export function getFilmsByType(type: FilmType): Film[] {
  return films.filter((film) => film.type === type);
}

export function getRegions(type?: FilmType): string[] {
  const source = type ? getFilmsByType(type) : films;
  return Array.from(new Set(source.map((film) => film.region)));
}

export function getYears(type?: FilmType): number[] {
  const source = type ? getFilmsByType(type) : films;
  return Array.from(new Set(source.map((film) => film.year))).sort((a, b) => b - a);
}

export function getGenres(type?: FilmType): string[] {
  const source = type ? getFilmsByType(type) : films;
  const set = new Set<string>();
  source.forEach((film) => film.genres.forEach((genre) => set.add(genre)));
  return Array.from(set);
}

export type SortKey = "latest" | "hot" | "rating";

export interface FilterOptions {
  type?: FilmType;
  region?: string;
  year?: string;
  genre?: string;
  sort?: SortKey;
}

export function filterFilms(options: FilterOptions): Film[] {
  const { type, region, year, genre, sort = "latest" } = options;
  const result = films.filter((film) => {
    if (type && film.type !== type) return false;
    if (region && film.region !== region) return false;
    if (year && String(film.year) !== year) return false;
    if (genre && !film.genres.includes(genre)) return false;
    return true;
  });

  if (sort === "rating") {
    return result.sort((a, b) => b.rating - a.rating);
  }
  if (sort === "hot") {
    return result.sort((a, b) => b.reviews.length - a.reviews.length || b.rating - a.rating);
  }
  return result.sort((a, b) => b.year - a.year || b.rating - a.rating);
}

export function newestFilms(limit = 6, type?: FilmType): Film[] {
  const source = type ? getFilmsByType(type) : films;
  return [...source]
    .sort((a, b) => b.year - a.year || b.rating - a.rating)
    .slice(0, limit);
}

export function getRecommendations(film: Film, limit = 4): Film[] {
  const scored = films
    .filter((item) => item.id !== film.id)
    .map((item) => {
      const sharedGenres = item.genres.filter((genre) => film.genres.includes(genre)).length;
      const sharedCast = item.cast.filter((name) => film.cast.includes(name)).length;
      const sameRegion = item.region === film.region ? 1 : 0;
      const sameType = item.type === film.type ? 1 : 0;
      return {
        film: item,
        score: sharedGenres * 3 + sharedCast * 4 + sameRegion + sameType + item.rating / 20,
      };
    })
    .sort((a, b) => b.score - a.score);

  return scored.slice(0, limit).map((entry) => entry.film);
}

export interface ReviewEntry {
  film: Film;
  review: FilmReview;
}

export function featuredReviews(limit = 6): ReviewEntry[] {
  const entries: ReviewEntry[] = [];
  films.forEach((film) => {
    film.reviews.forEach((review) => entries.push({ film, review }));
  });
  return entries.sort((a, b) => b.review.score - a.review.score).slice(0, limit);
}

export function searchFilms(keyword: string): Film[] {
  const query = keyword.trim().toLowerCase();
  if (!query) return [];
  return films.filter((film) => {
    const haystack = [
      film.title,
      film.en,
      film.director,
      film.region,
      ...film.cast,
      ...film.genres,
      ...film.tags,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(query);
  });
}

export const hotKeywords = ["暗河", "深海回声", "长夜将尽", "悬疑", "年代剧", "韩剧"];